import { createApi } from '@reduxjs/toolkit/query/react'
import { ExternalUserServiceIntegrationUpdate } from '@zocks-communications/client-sdk-js'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { removeEmptyItems } from '../../common/util'
import {
  Me,
  ServiceLocation,
  ShareType,
  User,
  UserCreateUpdatePayload,
  UserPromotePayload,
  UserShareSettings,
  UserShareSettingsUpdatePayload,
} from '../../models/User'
import { type SeatPurchase } from '../auth/auth'
import { BasePageRequest, BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { paginate } from '../utils'

// Define a service using a base URL and expected endpoints
const _BASE_URL = import.meta.env.REACT_APP_IAM_API_BASE_URL!

export interface GetUserByIdRequest extends BaseRequest {
  userId: string
}

export interface GetUsersRequest extends BasePageRequest {
  identityTypes?: string[]
  name?: string
  roomId?: string
  ids?: string[]
  statuses?: string[]
  roleGroups?: string[][]
  serviceId?: string
}

export interface GetAllUsersRequest extends BaseRequest {
  identityTypes?: string[]
  name?: string
  roomId?: string
  ids?: string[]
}

export interface GetAllServiceLocationsRequest extends BaseRequest {
  serviceId: string
}

export interface SaveUserByIdRequest extends BaseRequest {
  userId: string
  userData: UserCreateUpdatePayload
  keepEmptyItems?: boolean
}

export interface UpdateUserRolesRequest extends BaseRequest {
  userId: string
  roleIds: string[]
}

export interface BuyAndReplaceUserRolesRequest extends UpdateUserRolesRequest {
  purchase: SeatPurchase
}

export type RoleChangeReason = 'paymentRequired' | 'purchaseFailed' | 'purchaseUnconfirmed' | 'noSeatAvailable' | 'roleChangeFailed'

export interface BuyAndReplaceResult {
  purchased: SeatPurchase
  rolesApplied: boolean
  reason: RoleChangeReason | null
  checkoutRequired: boolean
  checkoutSessionUrl?: string
}

export interface UpdateUserIntegrationRequest extends BaseRequest {
  integrationName: string
  integrationId: string
  payload: ExternalUserServiceIntegrationUpdate
}

export type CreateUserIntegrationPayload =
  | OauthUserIntegrationPayload
  | OAuthUserCustomDomainIntegrationPayload
  | CreateUsernamePasswordUserIntegrationPayload
  | CreateVerificationUserIntegrationPayload
  | CreateServerAwareUsernamePasswordUserIntegrationPayload
  | CreateSiteNameOfficeNameUsernameUserIntegrationPayload
  | CreateStateAwareUserIntegrationPayload

export interface OauthUserIntegrationPayload {
  code: string
  redirectUri: string
  connectType?: string
}

export interface CreateUserIntegrationRequest extends BaseRequest {
  integrationName: string
  payload: CreateUserIntegrationPayload
}

export interface OAuthUserCustomDomainIntegrationPayload {
  code: string
  redirectUri: string
  customDomain: string
  environment?: string
}

export interface LocationAwareOauthUserIntegrationPayload extends OauthUserIntegrationPayload {
  accountsServer: string
  region: string
}

export interface CreateServerAwareUsernamePasswordUserIntegrationPayload {
  serverUrl: string
  username: string
  password: string
}

export interface CreateUsernamePasswordUserIntegrationPayload {
  username: string
  password: string
}

export interface CreateSiteNameOfficeNameUsernameUserIntegrationPayload {
  siteName: string
  officeName: string
  username: string
  connectType: string
}

export interface CreateVerificationUserIntegrationPayload {
  externalAccountId: string
  externalId: string
  verificationCode: string
}

export interface CreateStateAwareUserIntegrationPayload extends OauthUserIntegrationPayload {
  state: string
}

export interface DeleteUserRequest extends BaseRequest {
  userId: string
}

export interface GetUserIntegrationInfoRequest extends BaseRequest {
  userId: string
}

export type UserIntegrationIssue = {
  integrationId: string
  issue: string
  resolution: string
}

export interface GetUserIntegrationInfoResponse {
  problems: Array<UserIntegrationIssue>
  info: Array<UserIntegrationIssue>
}

export interface DeleteUserIntegrationRequest extends BaseRequest {
  payload: {
    userId: string
    integrationId: string
  }
}

export type ClaimRequestPayload = ClaimExternalUserRequestPayload | ClaimSmartOfficeRequestPayload | ClaimDelegatedEmailRequestPayload

export interface ClaimExternalUserRequestPayload {
  externalAccountId: string
  externalId: string
  verificationChannel: string
}

export interface ClaimSmartOfficeRequestPayload {
  siteName: string
  officeName: string
  username: string
  verificationCode?: string
}

export interface ClaimDelegatedEmailRequestPayload {
  delegatedEmail: string
  integrationId?: string
}

export interface ClaimExternalUserRequest extends BaseRequest {
  integrationName: string
  payload: ClaimRequestPayload
}

export interface SaveUserShareSettingsRequest extends BaseRequest {
  userId: string
  payload: UserShareSettingsUpdatePayload
}

export interface GetUserShareSettingsRequest extends BasePageRequest {
  userId: string
  shareType: ShareType
}

export interface GetAllUserSharesRequest extends BaseRequest {
  userId: string
  shareType: ShareType
}

export interface DeleteUserShareSettingsRequest extends BaseRequest {
  userId: string
  shareType: ShareType
}

export interface PromoteUserByIdRequest extends BaseRequest {
  userId: string
  userData: UserPromotePayload
}

export interface DeactivateUserByIdRequest extends BaseRequest {
  userId: string
}

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['me', 'user', 'userIntegrationInfo', 'userShares', 'userShareSettings'],
  endpoints: (builder) => ({
    getUsers: builder.query<BasePageResponse<User[]>, GetUsersRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/users`,
          params: {
            page: request.pageRequest.page,
            size: request.pageRequest.size,
            ...(request.identityTypes?.length && { identityTypes: request.identityTypes.join(',') }),
            name: request.name,
            ...(request.roomId && { roomId: request.roomId }),
            ...(request.statuses && { statuses: request.statuses.join(',') }),
            ...(request.roleGroups?.length && { roleGroups: JSON.stringify(request.roleGroups) }),
            ...(request.serviceId && { serviceId: request.serviceId }),
            ...(request.ids && { ids: request.ids.join() }),
          },
        }
      },
    }),
    getAllUsers: builder.query<BaseResponse<User[]>, GetAllUsersRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<User>(
          {
            url: `/accounts/${request.accountId}/users`,
            params: {
              name: request.name,
              ...(request.identityTypes?.length && { identityTypes: request.identityTypes.join(',') }),
              ...(request.roomId && { roomId: request.roomId }),
              ...(request.ids && { ids: request.ids.join() }),
            },
          },
          fetchWithBQ,
        )
      },
    }),
    getUserById: builder.query<BaseResponse<User>, GetUserByIdRequest>({
      query: (request) => `/accounts/${request.accountId}/users/${request.userId}`,
      providesTags: ['user'],
    }),
    getMe: builder.query<Me, BaseRequest>({
      query: (request) => `/accounts/${request.accountId}/users/me`,
      transformResponse: (response: BaseResponse<User>) => {
        const { name, firstName, lastName, username, id } = response.data
        return {
          ...response.data,
          me: name || (firstName && lastName ? `${firstName} ${lastName}` : username || id),
        }
      },
      providesTags: ['me'],
    }),
    getUserIntegrationInfo: builder.query<BaseResponse<GetUserIntegrationInfoResponse>, GetUserIntegrationInfoRequest>({
      query: (request) => `/accounts/${request.accountId}/user-integrations/${request.userId}/user-integration-info`,
      providesTags: ['userIntegrationInfo'],
    }),
    saveUserById: builder.mutation<BaseResponse<User>, SaveUserByIdRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/users/${request.userId}`,
        method: 'PATCH',
        body: request.keepEmptyItems ? request.userData : removeEmptyItems(request.userData),
      }),
      invalidatesTags: ['me'],
    }),
    deleteUserById: builder.mutation<BaseResponse<User>, DeleteUserRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/users/${request.userId}`,
        method: 'DELETE',
      }),
    }),
    updateUserRoles: builder.mutation<BaseResponse<User>, UpdateUserRolesRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/users/${request.userId}/roles`,
        method: 'PUT',
        body: { roleIds: request.roleIds },
      }),
      invalidatesTags: ['me', 'user'],
    }),
    buyAndReplaceUserRoles: builder.mutation<BaseResponse<BuyAndReplaceResult>, BuyAndReplaceUserRolesRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/users/${request.userId}/roles/buy-and-replace`,
        method: 'POST',
        body: { roleIds: request.roleIds, purchase: request.purchase },
      }),
      invalidatesTags: ['me', 'user'],
    }),
    updateUserIntegration: builder.mutation<void, UpdateUserIntegrationRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/user-integrations/${request.integrationName}/integrations/${request.integrationId}`,
        method: 'PATCH',
        body: request.payload,
      }),
      invalidatesTags: ['me', 'userIntegrationInfo'],
    }),
    createUserIntegration: builder.mutation<BaseResponse<User>, CreateUserIntegrationRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/user-integrations/${request.integrationName}/connect`,
        method: 'POST',
        body: request.payload,
      }),
      invalidatesTags: ['me', 'userIntegrationInfo'],
    }),
    claimExternalUser: builder.mutation<BaseResponse<User>, ClaimExternalUserRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/user-integrations/${request.integrationName}/claim`,
        method: 'POST',
        body: request.payload,
      }),
      invalidatesTags: ['me', 'userIntegrationInfo'],
    }),
    getAllServiceLocations: builder.query<BaseResponse<ServiceLocation[]>, GetAllServiceLocationsRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<ServiceLocation>(
          {
            url: `/accounts/${request.accountId}/user-integrations/${request.serviceId}/locations`,
          },
          fetchWithBQ,
        )
      },
    }),
    deleteUserIntegration: builder.mutation<void, DeleteUserIntegrationRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/user-integrations/disconnect`,
        method: 'POST',
        body: request.payload,
      }),
      invalidatesTags: ['me', 'userIntegrationInfo'],
    }),
    getUserHash: builder.query<string, BaseRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/users/me/hash`,
        method: 'POST',
      }),
      transformResponse: (response: BaseResponse<string>) => {
        return response.data
      },
    }),
    saveUserShareSettings: builder.mutation<BasePageResponse<UserShareSettings[]>, SaveUserShareSettingsRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/users/${request.userId}/share-settings`,
        method: 'PUT',
        body: request.payload,
      }),

      invalidatesTags: ['userShareSettings', 'userShares'],
    }),
    getUserShareSettings: builder.query<BasePageResponse<UserShareSettings[]>, GetUserShareSettingsRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/users/${request.userId}/share-settings`,
        method: 'GET',
        params: {
          shareType: request.shareType,
          page: request.pageRequest.page,
          size: request.pageRequest.size,
        },
      }),
      providesTags: ['userShareSettings'],
    }),
    getUserShare: builder.query<BasePageResponse<UserShareSettings[]>, GetUserShareSettingsRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/users/${request.userId}/share-settings/shared-with-me`,
        method: 'GET',
        params: {
          shareType: request.shareType,
          page: request.pageRequest.page,
          size: request.pageRequest.size,
        },
      }),
      providesTags: ['userShareSettings'],
    }),
    getAllUserShares: builder.query<BaseResponse<UserShareSettings[]>, GetAllUserSharesRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<UserShareSettings>(
          {
            url: `/accounts/${request.accountId}/users/${request.userId}/share-settings/shared-with-me`,
            params: { shareType: request.shareType },
          },
          fetchWithBQ,
        )
      },
    }),
    getAllUserShareSettings: builder.query<BaseResponse<UserShareSettings[]>, GetAllUserSharesRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<UserShareSettings>(
          {
            url: `/accounts/${request.accountId}/users/${request.userId}/share-settings`,
            params: { shareType: request.shareType },
          },
          fetchWithBQ,
        )
      },
      providesTags: ['userShareSettings'],
    }),
    deleteUserShareSettings: builder.mutation<void, DeleteUserShareSettingsRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/users/${request.userId}/share-settings`,
        method: 'DELETE',
        params: {
          shareType: request.shareType,
        },
        validateStatus: (response) => (response.status >= 200 && response.status <= 299) || response.status === 404,
      }),
      invalidatesTags: ['userShareSettings'],
    }),
    promoteUserById: builder.mutation<BaseResponse<User>, PromoteUserByIdRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/users/${request.userId}/promote`,
        method: 'POST',
        body: removeEmptyItems(request.userData),
      }),
    }),
    deactivateUserById: builder.mutation<BaseResponse<User>, DeactivateUserByIdRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/users/${request.userId}/deactivate`,
        method: 'POST',
      }),
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useGetAllUsersQuery,
  useLazyGetAllUsersQuery,
  useLazyGetUserByIdQuery,
  useGetUserByIdQuery,
  useGetMeQuery,
  useLazyGetMeQuery,
  useSaveUserByIdMutation,
  useDeleteUserByIdMutation,
  useUpdateUserRolesMutation,
  useBuyAndReplaceUserRolesMutation,
  useUpdateUserIntegrationMutation,
  useCreateUserIntegrationMutation,
  useClaimExternalUserMutation,
  useGetAllServiceLocationsQuery,
  useGetUserHashQuery,
  useGetUserIntegrationInfoQuery,
  useDeleteUserIntegrationMutation,
  useGetUserShareQuery,
  useGetAllUserSharesQuery,
  useGetAllUserShareSettingsQuery,
  useSaveUserShareSettingsMutation,
  useGetUserShareSettingsQuery,
  useDeleteUserShareSettingsMutation,
  usePromoteUserByIdMutation,
  useDeactivateUserByIdMutation,
} = userApi
